// 注：当前组件是对src/pages/rank-scale/components/RankTab/index.tsx重构的结果
import { CategoryHotRankTabVO, } from "@/api/rank";
import { platform } from "@/utils/platform";
import { getRatio, pxtorem } from "@/utils/pxtorem";
import React, { useEffect, useRef, useState } from "react";
import styles from "./index.module.less";

interface TwoLevelTabProps {
  level1Opts: CategoryHotRankTabVO[];
  selectedFirstIndex: number;
  selectedSecondIndex: number;
  showTabPop: boolean;
  onFirstTabClick: (index: number) => void;
  onSecondTabClick: (index: number) => void;
  onShowTabPop: (show: boolean) => void;
  // 触顶变化回调
  onStickyChange?: (isSticky: boolean) => void;
  statusBarHeight?: number;
  style?: React.CSSProperties;//最外层元素的样式
  zIndex?: number;//组件层级
  fixedStyle?: React.CSSProperties;//吸顶后，吸顶内容的样式
  fixedZIndex?: number;//吸顶后，吸顶内容的z-index
}

const TwoLevelTab: React.FC<TwoLevelTabProps> = ({
  level1Opts,
  selectedFirstIndex,
  selectedSecondIndex,
  showTabPop,
  onFirstTabClick,
  onSecondTabClick,
  onShowTabPop,
  onStickyChange,
  statusBarHeight = 50,
  fixedStyle={},
  style={},
  zIndex=20,
  fixedZIndex=20,
}) => {
  const tabsRef = useRef<HTMLDivElement>(null);
  const stickyTriggerRef = useRef<HTMLDivElement>(null);
  const twoLevelTabRef = useRef<HTMLDivElement>(null);
  // 是否吸顶了
  const [isFixed, setIsFixed] = useState(false);
  const [tabsHeight, setTabsHeight] = useState(0);
  // sticky的top阈值，H5吸顶时，上方还有一个高度50的“全场100%正品”。App则是吸顶状态栏NavHeader4App的高度
  const stickyTop = platform.isH5 ? (50 * getRatio()) : platform.isMini ? 0 : ((44 + statusBarHeight) * getRatio());

  // 获取当前选中的一级tab
  const currentFirstTab = level1Opts[selectedFirstIndex];
  
  // 获取当前的二级tabs
  const currentSecondTabs = currentFirstTab?.tabItemVOList || [];

  // 监听firstLevelTabs变化，重新测量tabs高度
  useEffect(() => {
    if (tabsRef.current) {
      // 使用 setTimeout 确保DOM更新完成后再测量
      setTimeout(() => {
        if (tabsRef.current) {
          const height = tabsRef.current.offsetHeight;
          setTabsHeight(height);
        }
      }, 0);
    }
  }, [level1Opts, selectedFirstIndex]);

    // 监听二级tab触顶，触顶后需要fixed固定，并改变背景色
  useEffect(() => {
    if (!stickyTriggerRef.current || !onStickyChange) {
      return;
    }

    let observer: IntersectionObserver | null = null;

    // 延迟初始化，避免页面加载时立即触发
    const timer = setTimeout(() => {
      if (!stickyTriggerRef.current) {
        return;
      }

      observer = new IntersectionObserver(
        ([dom]) => {
          if(!dom) {
            console.error("监听吸顶异常，没有找到容器dom");
            return;
          }
          
          // 更精确的判断：当触发器元素的底部超过了rootMargin定义的边界时才认为需要吸顶
          const shouldBeFixed = !dom.isIntersecting;
          
          setIsFixed(shouldBeFixed);
          onStickyChange(shouldBeFixed);
        },
        {
          threshold: [0.0],//threshold: 0.0 只要元素有任何部分在观察区域内就算可见
          rootMargin: `-${stickyTop}px 0px 0px 0px`//触发边界，-stickyTop表示触发时，元素距离根元素顶部stickyTop像素的距离触发
        }
      );

      observer.observe(stickyTriggerRef.current);
    }, 200); // 延迟100ms初始化

    return () => {
      clearTimeout(timer);
      // 清理观察器
      if (observer) {
        observer.disconnect();
      }
    };
  }, [onStickyChange, stickyTop]);

  /**
   * 吸顶dom样式,吸顶时固定定位，否则静态定位
   * 这里没有使用sticky,而是使用fixed,因为sticky没法在吸顶后修改背景色
   * 所以这里结合IntersectionObserver和fixed来实现sticky吸顶效果
   */
  const fixFragmentStyle = isFixed ? {
    position: "fixed" as const,
    top: stickyTop,
    left: 0,
    right: 0,
    width: "100%",
    ...fixedStyle,
    zIndex: fixedZIndex || 20
  } : {};

  // 类目二级tab后的icon
  const getLevel2IconFragment = (categoryScene=1) => {
    // 只有多榜单场景才显示展开按钮
    if(categoryScene === 4){
      return null;
    }

    return (
      <img
        onClick={(e) => {
          e.stopPropagation();
          onShowTabPop(!showTabPop);
        }}
        className={styles.btn}
        id="modal_content"
        alt={showTabPop ? "收起" : "展开"}
        src={showTabPop
          ? "https://akim-oss.aikucun.com/38f258b236203d761b8cd7711e6b109335dcc09c_1742203470355_15.png"
          : "https://akim-oss.aikucun.com/e5df14d00708c3e59aecc79227c867c87d73a747_1742203402491_24.png"
        }
      />
    );
  };

  return (
    <div 
      className={styles.twoLevelTab} 
      ref={twoLevelTabRef}
      style={{ 
        height: isFixed ? tabsHeight : "auto",
        ...style 
      }}
    >
      {/* 滚动sticky触发dom,用于判断上边缘触顶 */}
      <div className="stickyTrigger" ref={stickyTriggerRef} style={{ height: "1px", width: "100%" }}></div>

      {/* 内容吸顶时，父组件高度不塌陷 */}
      <div 
        className={styles.tabsBox + " " + (isFixed ? styles.fixed : "")} 
        ref={tabsRef} 
        style={fixFragmentStyle}
      >
        {/* 一级Tab */}
        {level1Opts && level1Opts.length > 0 && (
          <div className={styles.firstTabTile +" " +(level1Opts.length===1?styles.hidden:"")}>
            {level1Opts.map((item, index) => {
              return (
                <div
                  className={`${styles.level1Item} ${index === selectedFirstIndex ? styles.active : ""}`}
                  id={`first-category-${index}`}
                  key={index}
                  onClick={() => onFirstTabClick(index)}
                >
                  <div className={styles.level1MainTitle}>{item?.categoryName || ""}</div>
                  <div className={styles.level1SubTitle}>{item?.subCategoryName || ""}</div>
                  {/* {index === selectedFirstIndex && <div className={styles.firstSelectedLine} />} */}
                </div>
              );
            })}
          </div>
        )}

        {/* 二级Tab */}
        {currentSecondTabs && currentSecondTabs.length > 0 && (
          <div className={styles.secondLevelTabDiv + " " + (currentFirstTab?.categoryScene !== 4  ? styles.categoryLevel2 : styles.tagLevel2)}>
            <div className={styles.secondTabTile}>
              {currentSecondTabs.map((item, index) => {
                return (
                  <div
                    className={`${styles.level2Item} ${index === selectedSecondIndex ? styles.active : ""}`}
                    id={`second-category-${index}`}
                    key={index}
                    onClick={() => onSecondTabClick(index)}
                  >
                    <div className={styles.level2Txt}>{item?.tabName || ""}</div>
                    {/* 单榜单类型二级tab的高亮下划线 */}
                    {currentFirstTab?.categoryScene ===4 && index === selectedSecondIndex && (<div className={styles.activeLine} />)}
                  </div>
                );
              })}
            </div>
            
            {/* 展开/收起按钮 - 与二级tab在同一高度 */}
            {getLevel2IconFragment(currentFirstTab?.categoryScene)}
          </div>
        )}

        {/* 二级分类Tab弹窗 - 显示类目tabs选项（二级tabs） */}
        {showTabPop &&currentSecondTabs && currentSecondTabs.length > 0 && (
          <div
            className={styles.tabModal}
            style={{
              top: "100%",
              height: "calc(100vh - 145px)",
              zIndex: zIndex || 99,
              position: "absolute",
              width: "100%",
            }}
            onClick={() => onShowTabPop(false)}
          >
            <div
              className={styles.content}
              id="categories-in-modal"
              style={{
                transform: "translateY(0px)",
                transition: "transform 0.3s",
              }}
            >
              {currentSecondTabs.map((item, index) => {
                return (
                  <div
                    key={index}
                    id="modal_content"
                    className={
                      styles.category +
                      " " +
                      (index === selectedSecondIndex ? styles.category_selected : styles.uncategory_selected)
                    }
                    onClick={() => onSecondTabClick(index)}
                  >
                    <div id="modal_content" className={(index === selectedSecondIndex ? styles.text : styles.untext)}>
                      {item?.tabName || ""}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className={styles.popBottom} />
          </div>
        )}
      </div>
    </div>
  );
};

export default TwoLevelTab;
